import { useEffect, useMemo, useState } from 'react';
import {
  store,
  update,
  IGlobalState,
  selectedApp,
  useStore,
} from '../../store';
import {
  getBuildCount,
  getBuildCountCreatedAfter,
  getBuildsCreatedBetween,
  getBuildsOfVersion,
  getLatestReleaseBefore,
  getLatestSucceededBuild,
  getOlderBuilds,
  getRecentlyReleasedBuilds,
  onBuildUpdate,
  onRecentBuildsUpdate,
  setBuildsReleaseDismissed,
} from '../firebase/buildsStorage';
import { Build, IApp } from '@todesktop/shared';
import type { QueryDocumentSnapshot, QuerySnapshot } from 'firebase/firestore';
import { getPriorRelease, parseDate, returnSortedBuilds } from '../util';

/** Builds kept live at the top of the list, and loaded per page after that */
export const BUILDS_PAGE_SIZE = 30;
/** Released builds loaded to compare a build with */
const RECENT_RELEASES = 10;

export const initialBuildState: IGlobalState['buildState'] = {
  builds: {},
  hasBuildsDataLoaded: false,
  selectedBuild: '',
  releasedBuildId: '',
  hasOlderBuilds: false,
  isLoadingOlderBuilds: false,
  oldestListedCreatedAt: '',
  buildCount: null,
  missingBuildIds: {},
  hasRecentReleasesLoaded: false,
};

/**
 * Listeners and paging state for the selected app's builds. Builds are loaded
 * as a live window of the newest builds plus older pages on demand, because
 * apps can have thousands of large build documents. Builds needed outside
 * that window (the released build, partial releases and any build a page
 * shows) are watched by ID.
 */
interface BuildsSession {
  appId: string;
  userId: string;
  unsubscribers: (() => void)[];
  /** Watched builds by ID, with when each first loaded or stopped */
  watched: Map<string, WatchedBuild>;
  /** The oldest build loaded through paging, to page after */
  oldestDoc?: QueryDocumentSnapshot<Build>;
  /** Whether every build has been loaded */
  allLoaded: boolean;
  recentReleases?: Promise<void>;
  /** Prior releases loaded per build ID */
  priorReleases: Map<string, Promise<void>>;
  /**
   * Loads of the builds created in a range of dates, while in flight, so
   * charts of the same period share one
   */
  buildsCreatedBetween: Map<string, Promise<Build[]>>;
  latestSucceededBuild?: Promise<void>;
  /** Number of build counts requested, to ignore stale responses */
  countRequests: number;
}

interface WatchedBuild {
  unsubscribe: () => void;
  loaded: Promise<void>;
}

let session: BuildsSession | null = null;

const last = <T>(items: T[]): T | undefined => items[items.length - 1];

/** Builds watched by components, as `appId/buildId` → number of watchers */
const wantedBuilds = new Map<string, number>();

export const resetBuildSubscriptions = () => {
  if (session) {
    session.unsubscribers.forEach((unsubscribe) => unsubscribe());
    session.watched.forEach(({ unsubscribe }) => unsubscribe());
    session = null;
  }
  resetBuildState();
};

// this function re-executes whenever the selectedApp has changed
export const subscribeToBuildCollectionUpdates = (appId: IApp['id']) => {
  resetBuildSubscriptions();

  const state = store.getRawState();
  const app = selectedApp(state);

  // unsubscribe from old listeners, but do not subscribe to new ones
  if (!app || app.appType !== 'electron') {
    return setBuildsDataAsLoaded();
  }

  const user = state.appToUser[appId];
  if (!user) return;

  const current: BuildsSession = {
    appId,
    userId: user.id,
    unsubscribers: [],
    watched: new Map(),
    allLoaded: false,
    priorReleases: new Map(),
    buildsCreatedBetween: new Map(),
    countRequests: 0,
  };
  session = current;

  if (app.meta?.latestReleaseBuildId) {
    update((draft) => {
      draft.buildState.releasedBuildId = app.meta!.latestReleaseBuildId!;
    });
  }

  // The app names its released build, so load it (and the builds of partial
  // releases) alongside the newest builds rather than after them
  const pinnedBuildsLoaded = Promise.all([
    syncWatchedBuilds(current),
    loadReleasedBuildId(current).then(() => syncWatchedBuilds(current)),
  ]).catch((error) => console.error('Failed to load builds', error));

  let isFirstSnapshot = true;
  current.unsubscribers.push(
    onRecentBuildsUpdate(
      current.userId,
      appId,
      BUILDS_PAGE_SIZE,
      (snapshot) => {
        if (session !== current) return;
        applyRecentBuilds(snapshot, isFirstSnapshot);
        if (isFirstSnapshot) {
          isFirstSnapshot = false;
          void onFirstRecentBuilds(current, snapshot, pinnedBuildsLoaded);
          return;
        }
        extendListedBuilds(current, snapshot);
        if (changesBuildCount(snapshot)) void refreshBuildCount(current);
      },
      (error) => {
        console.error('Failed to load builds', error);
        if (session === current) setBuildsDataAsLoaded();
      },
    ),
  );

  // Keep the released build and partial releases watched as they change
  current.unsubscribers.push(
    store.subscribe(
      (s) => getPinnedBuildIds(s),
      () => void syncWatchedBuilds(current),
    ),
  );
};

const onFirstRecentBuilds = async (
  current: BuildsSession,
  snapshot: QuerySnapshot<Build>,
  pinnedBuildsLoaded: Promise<unknown>,
) => {
  current.oldestDoc = last(snapshot.docs);
  current.allLoaded = snapshot.size < BUILDS_PAGE_SIZE;
  update((draft) => {
    draft.buildState.hasOlderBuilds = !current.allLoaded;
    draft.buildState.oldestListedCreatedAt =
      current.oldestDoc?.data().createdAt ?? '';
  });
  void refreshBuildCount(current);

  await pinnedBuildsLoaded;
  try {
    // And any build a component asked for meanwhile
    await syncWatchedBuilds(current);
  } catch (error) {
    console.error('Failed to load builds', error);
  }
  if (session === current) setBuildsDataAsLoaded();
};

/**
 * After a deletion, an older build moves into the window. List it, and page
 * on from it.
 */
const extendListedBuilds = (
  current: BuildsSession,
  snapshot: QuerySnapshot<Build>,
) => {
  const oldestInWindow = last(snapshot.docs);
  if (current.allLoaded || !oldestInWindow || !current.oldestDoc) return;

  const { createdAt } = oldestInWindow.data();
  if (parseDate(createdAt) >= parseDate(current.oldestDoc.data().createdAt)) {
    return;
  }
  current.oldestDoc = oldestInWindow;
  update((draft) => {
    draft.buildState.oldestListedCreatedAt = createdAt;
  });
};

/** Merges a snapshot of the newest builds into the store */
const applyRecentBuilds = (
  snapshot: QuerySnapshot<Build>,
  isFirstSnapshot: boolean,
) => {
  const changes = isFirstSnapshot
    ? snapshot.docs.map((doc) => ({ doc, type: 'added' as const }))
    : snapshot.docChanges();
  if (!changes.length) return;

  update((draft) => {
    for (const { doc, type } of changes) {
      const build = doc.data();
      if (type !== 'removed') {
        draft.buildState.builds[build.id] = build;
      } else if (isDeletion(doc, snapshot)) {
        delete draft.buildState.builds[build.id];
      }
      // Otherwise newer builds pushed it out of the window; keep it listed
    }
  });
};

/**
 * Whether `a` comes after `b` in the window's order: `createdAt` descending,
 * then document ID descending, as Firestore breaks ties by name
 */
const isOlderInWindow = (
  a: QueryDocumentSnapshot<Build>,
  b: QueryDocumentSnapshot<Build>,
) => {
  const aCreatedAt = a.data().createdAt;
  const bCreatedAt = b.data().createdAt;
  return aCreatedAt < bCreatedAt || (aCreatedAt === bCreatedAt && a.id < b.id);
};

/**
 * Builds leave a full window when newer builds push them out, so a removed
 * build was only deleted if the window isn't full or it comes before the
 * oldest build still in the window.
 */
const isDeletion = (
  doc: QueryDocumentSnapshot<Build>,
  snapshot: QuerySnapshot<Build>,
) => {
  const oldestInWindow = last(snapshot.docs);
  return (
    snapshot.size < BUILDS_PAGE_SIZE ||
    !oldestInWindow ||
    !isOlderInWindow(doc, oldestInWindow)
  );
};

const changesBuildCount = (snapshot: QuerySnapshot<Build>) =>
  snapshot
    .docChanges()
    .some(
      ({ doc, type }) =>
        type === 'added' || (type === 'removed' && isDeletion(doc, snapshot)),
    );

const refreshBuildCount = async (current: BuildsSession) => {
  current.countRequests += 1;
  const request = current.countRequests;
  try {
    const count = await getBuildCount(current.userId, current.appId);
    // Counts can resolve out of order when builds change in quick succession
    if (session !== current || request !== current.countRequests) return;
    update((draft) => {
      draft.buildState.buildCount = count;
    });
  } catch (error) {
    console.error('Failed to count builds', error);
  }
};

/** Older apps may not have `latestReleaseBuildId`, so find the newest release */
const loadReleasedBuildId = async (current: BuildsSession) => {
  if (store.getRawState().buildState.releasedBuildId) return;

  const [latestRelease] = await getRecentlyReleasedBuilds(
    current.userId,
    current.appId,
    1,
  );
  if (session !== current || !latestRelease) return;
  update((draft) => {
    if (!draft.buildState.releasedBuildId) {
      draft.buildState.releasedBuildId = latestRelease.id;
    }
  });
};

/** Adds builds loaded once, without replacing newer copies from listeners */
const mergeBuilds = (builds: Build[]) => {
  if (!builds.length) return;
  update((draft) => {
    for (const build of builds) {
      if (!draft.buildState.builds[build.id]) {
        draft.buildState.builds[build.id] = build;
      }
    }
  });
};

/** The released build and the builds of partial releases */
const getPinnedBuildIds = (state: IGlobalState): string[] => {
  const ids = (selectedApp(state)?.meta?.releaseRedirections ?? []).flatMap(
    (rule) =>
      rule.rule === 'buildByIp' || rule.rule === 'buildByPlatform'
        ? [rule.buildId]
        : [],
  );
  return [state.buildState.releasedBuildId, ...ids].filter(Boolean);
};

/**
 * Watches the pinned builds and the builds components asked for, and stops
 * watching the rest. Resolves once every wanted build has loaded.
 */
const syncWatchedBuilds = (current: BuildsSession): Promise<void> => {
  if (session !== current) return Promise.resolve();

  const wanted = new Set(getPinnedBuildIds(store.getRawState()));
  const prefix = `${current.appId}/`;
  for (const key of wantedBuilds.keys()) {
    if (key.startsWith(prefix)) wanted.add(key.slice(prefix.length));
  }

  for (const [buildId, { unsubscribe }] of current.watched) {
    if (!wanted.has(buildId)) {
      unsubscribe();
      current.watched.delete(buildId);
    }
  }

  const loads: Promise<void>[] = [];
  for (const buildId of wanted) {
    const watched = current.watched.get(buildId);
    if (watched) {
      // Also wait for a build an earlier call started loading
      loads.push(watched.loaded);
      continue;
    }
    let resolve!: () => void;
    const loaded = new Promise<void>((res) => {
      resolve = res;
    });
    const stopListening = onBuildUpdate(
      current.userId,
      buildId,
      current.appId,
      (build) => {
        if (session === current) setWatchedBuild(buildId, build);
        resolve();
      },
      (error) => {
        console.error(`Failed to load build ${buildId}`, error);
        if (session === current) setWatchedBuild(buildId, undefined);
        resolve();
      },
    );
    // Waiting stops along with watching
    const unsubscribe = () => {
      stopListening();
      resolve();
    };
    current.watched.set(buildId, { unsubscribe, loaded });
    loads.push(loaded);
  }
  return Promise.all(loads).then(() => undefined);
};

const setWatchedBuild = (buildId: string, build: Build | undefined) =>
  update((draft) => {
    if (build) {
      draft.buildState.builds[buildId] = build;
      delete draft.buildState.missingBuildIds[buildId];
    } else {
      delete draft.buildState.builds[buildId];
      draft.buildState.missingBuildIds[buildId] = true;
    }
  });

/**
 * Keeps a build loaded and live while it's watched, even if it's older than
 * the builds loaded for the list. Returns a function that stops watching.
 */
export const watchBuild = (appId: string, buildId: string) => {
  const key = `${appId}/${buildId}`;
  wantedBuilds.set(key, (wantedBuilds.get(key) ?? 0) + 1);
  if (session?.appId === appId) void syncWatchedBuilds(session);

  return () => {
    const count = (wantedBuilds.get(key) ?? 1) - 1;
    if (count > 0) {
      wantedBuilds.set(key, count);
      return;
    }
    wantedBuilds.delete(key);
    if (session?.appId === appId) void syncWatchedBuilds(session);
  };
};

/** Watches builds while the component is mounted */
export const useWatchedBuilds = (
  appId: string | undefined,
  buildIds: (string | undefined)[],
) => {
  const key = buildIds.filter(Boolean).join(',');
  useEffect(() => {
    if (!appId || !key) return undefined;
    const unwatchers = key
      .split(',')
      .map((buildId) => watchBuild(appId, buildId));
    return () => unwatchers.forEach((unwatch) => unwatch());
  }, [appId, key]);
};

/** Loads the next page of older builds for the list */
export const loadOlderBuilds = async () => {
  const current = session;
  const { buildState } = store.getRawState();
  if (!current || !buildState.hasOlderBuilds || buildState.isLoadingOlderBuilds)
    return;

  update((draft) => {
    draft.buildState.isLoadingOlderBuilds = true;
  });
  try {
    if (!current.oldestDoc) return;
    const snapshot = await getOlderBuilds(
      current.userId,
      current.appId,
      current.oldestDoc,
      BUILDS_PAGE_SIZE,
    );
    if (session !== current) return;
    current.oldestDoc = last(snapshot.docs) ?? current.oldestDoc;
    current.allLoaded = snapshot.size < BUILDS_PAGE_SIZE;

    mergeBuilds(snapshot.docs.map((doc) => doc.data()));
    update((draft) => {
      draft.buildState.hasOlderBuilds = !current.allLoaded;
      draft.buildState.oldestListedCreatedAt = current.allLoaded
        ? ''
        : (current.oldestDoc?.data().createdAt ?? '');
    });
  } catch (error) {
    console.error('Failed to load older builds', error);
  } finally {
    if (session === current) {
      update((draft) => {
        draft.buildState.isLoadingOlderBuilds = false;
      });
    }
  }
};

/** Loads the latest releases, to compare builds with the release before them */
export const loadRecentReleases = () => {
  const current = session;
  if (!current) return Promise.resolve();

  current.recentReleases ??= getRecentlyReleasedBuilds(
    current.userId,
    current.appId,
    RECENT_RELEASES,
  )
    .then((builds) => {
      if (session === current) mergeBuilds(builds);
    })
    .catch((error) => {
      console.error('Failed to load releases', error);
    })
    .finally(() => {
      if (session !== current) return;
      update((draft) => {
        draft.buildState.hasRecentReleasesLoaded = true;
      });
    });
  return current.recentReleases;
};

/** Loads the latest releases once the app's builds have loaded */
export const useRecentReleases = (appId: string | undefined) => {
  const hasBuildsDataLoaded = useStore(
    (state) => state.buildState.hasBuildsDataLoaded,
  );
  useEffect(() => {
    if (appId && hasBuildsDataLoaded && session?.appId === appId) {
      void loadRecentReleases();
    }
  }, [appId, hasBuildsDataLoaded]);
};

/**
 * The release before a build, to compare it with. Loads it if needed, as it
 * may be older than the loaded builds.
 */
export const usePriorRelease = (build: Build | undefined) => {
  const builds = useStore((state) => state.buildState.builds);
  const buildId = build?.id;
  const startedAt = build?.startedAt;

  useEffect(() => {
    const current = session;
    if (!current || !buildId || !startedAt) return;
    if (current.priorReleases.has(buildId)) return;

    current.priorReleases.set(
      buildId,
      getLatestReleaseBefore(current.userId, current.appId, startedAt)
        .then((release) => {
          if (release && session === current) mergeBuilds([release]);
        })
        .catch((error) => console.error('Failed to load release', error)),
    );
  }, [buildId, startedAt]);

  return useMemo(
    () => (build ? getPriorRelease(build, builds) : null),
    [build, builds],
  );
};

/**
 * Loads the newest successful build, for when newer failed or cancelled
 * builds fill the loaded builds
 */
export const useLatestSucceededBuild = (
  appId: string | undefined,
  enabled: boolean,
) => {
  useEffect(() => {
    const current = session;
    if (!enabled || !current || current.appId !== appId) return;

    current.latestSucceededBuild ??= getLatestSucceededBuild(
      current.userId,
      current.appId,
    )
      .then((build) => {
        if (build && session === current) mergeBuilds([build]);
      })
      .catch((error) => console.error('Failed to load build', error));
  }, [appId, enabled]);
};

/**
 * The builds that can be listed newest first without gaps: builds loaded by
 * ID that are older than the loaded pages are left out.
 */
export const selectListedBuilds = (buildState: IGlobalState['buildState']) => {
  const sorted = returnSortedBuilds(buildState.builds);
  if (!buildState.hasOlderBuilds) return sorted;

  const oldest = parseDate(buildState.oldestListedCreatedAt);
  return sorted.filter((build) => parseDate(build.createdAt) >= oldest);
};

/**
 * Gets the selected app's builds created in a period, including builds older
 * than the ones loaded for the list. Returns null if no app is selected.
 */
export const loadBuildsCreatedBetween = async (start: Date, end: Date) => {
  const current = session;
  if (!current) return null;
  const key = `${start.toISOString()}/${end.toISOString()}`;
  let builds = current.buildsCreatedBetween.get(key);
  if (!builds) {
    builds = getBuildsCreatedBetween(
      current.userId,
      current.appId,
      start.toISOString(),
      end.toISOString(),
    );
    current.buildsCreatedBetween.set(key, builds);
    // Only loads in flight are shared, so later loads see newer builds
    const settle = () => current.buildsCreatedBetween.delete(key);
    builds.then(settle, settle);
  }
  return builds;
};

/** How many builds were created after `build`, or null while counting */
export const useNumberOfLaterBuilds = (build: Build): number | null => {
  const buildState = useStore((state) => state.buildState);
  const listed = useMemo(() => selectListedBuilds(buildState), [buildState]);
  const index = listed.findIndex(({ id }) => id === build.id);
  const [counted, setCounted] = useState<{ count: number; id: string }>();

  // Count on the server when the build is older than the listed builds
  useEffect(() => {
    const current = session;
    if (index !== -1 || !current) return undefined;

    let active = true;
    getBuildCountCreatedAfter(current.userId, current.appId, build.createdAt)
      .then((count) => {
        if (active) setCounted({ count, id: build.id });
      })
      .catch((error) => console.error('Failed to count builds', error));
    return () => {
      active = false;
    };
  }, [index, build.id, build.createdAt]);

  if (index !== -1) return index;
  return counted?.id === build.id ? counted.count : null;
};

const resetBuildState = () =>
  update((draft) => {
    draft.buildState = initialBuildState;
  });

export const selectBuild = (buildId: Build['id']) =>
  update((draft) => {
    draft.buildState.selectedBuild = buildId;
  });

export const selectedBuild = (state: IGlobalState) =>
  selectBuildFromID(state.buildState.selectedBuild)(state);

export const selectedReleasedBuild = (state: IGlobalState) =>
  selectBuildFromID(state.buildState.releasedBuildId)(state);

export const selectBuildFromID =
  (buildId: Build['id']) => (state: IGlobalState) => {
    const foundBuild = state.buildState.builds[buildId];

    if (foundBuild) {
      return foundBuild;
    }

    return null;
  };

export const setBuildsDataAsLoaded = () => {
  update((draft) => {
    draft.buildState.hasBuildsDataLoaded = true;
  });
};

/**
 * The IDs of a version's builds, dismissed or not. Loaded from Firestore, as
 * older builds of the version may not be loaded.
 */
const versionBuildIds = async (
  ownerId: string,
  appId: string,
  appVersion: string,
  { dismissed }: { dismissed: boolean },
) =>
  (await getBuildsOfVersion(ownerId, appId, appVersion))
    .filter((b) => Boolean(b.releaseDismissedAt) === dismissed)
    .map((b) => b.id);

/**
 * Marks every build of `appVersion` as one the user won't release, which
 * keeps the version out of "Ready to release". Builds made later aren't
 * affected.
 */
export const dismissVersionRelease = async (
  appId: IApp['id'],
  appVersion: Build['appVersion'],
) => {
  const state = store.getRawState();
  const owner = state.appToUser[appId];
  const { user } = state;
  if (!owner || !user || !appVersion) return;
  const name = [user.firstName, user.lastName].filter(Boolean).join(' ');
  await setBuildsReleaseDismissed(
    owner.id,
    appId,
    await versionBuildIds(owner.id, appId, appVersion, { dismissed: false }),
    {
      at: new Date().toISOString(),
      by: {
        displayName: name.trim() || user.email || user.id,
        userId: user.id,
      },
    },
  );
};

export const undoVersionReleaseDismissal = async (
  appId: IApp['id'],
  appVersion: Build['appVersion'],
) => {
  const owner = store.getRawState().appToUser[appId];
  if (!owner || !appVersion) return;
  await setBuildsReleaseDismissed(
    owner.id,
    appId,
    await versionBuildIds(owner.id, appId, appVersion, { dismissed: true }),
  );
};
